import { errorUtil } from '../util/error-util'
import { HttpResponse, authDaoFactory } from '@grndhouse/react-common'

export type AuthDaoLoginResult = {
  accessToken: string
  refreshToken: string
  redirectUrl: string
}

export type AuthDaoRegisterResult = {
  accessToken: string
  refreshToken: string
  redirectUrl: string
}

export type User = {
  id: string
  email: string
  name: string
  phoneNumber?: string
  isEmailVerified: boolean
  isPhoneNumberVerified: boolean
  roles?: { name: string }[]
  isInactive: boolean
}

const dao = authDaoFactory({
  onError: async (error: Error) => {
    errorUtil.handleHttpError(error)
  },
})
export const authDao = {
  ...dao,
  login: async (params: { email: string; password: string; clientGuid: string }): Promise<AuthDaoLoginResult> => {
    const result = await dao._http.post<HttpResponse<AuthDaoLoginResult>>('/login', params)
    return result.data.data[0]
  },
  register: async (params: { email: string }): Promise<void> => {
    await dao._http.post<HttpResponse<{ expireDate: number }>>('/register', params)
  },
  registerVerifyEmail: async (params: { email: string; code: string; clientGuid: string }): Promise<AuthDaoRegisterResult> => {
    const result = await dao._http.post<HttpResponse<AuthDaoRegisterResult>>('/register/verify-email', params)
    return result.data.data[0]
  },
  setInitialPassword: async (params: { password: string }): Promise<void> => {
    const url = 'users/me/set-initial-password'
    await dao._http.post<HttpResponse<void>>(url, params)
  },
  getUsers: async (): Promise<User[]> => {
    const result = await dao._http.get<HttpResponse<User>>('/users')
    return result.data.data
  },
  partialUpdateUser: async (params: { userId: string; data: Partial<User> }): Promise<User> => {
    const { userId, data } = params
    const result = await dao._http.patch<HttpResponse<User>>(`/users/${userId}`, data)
    return result.data.data[0]
  },
  requestResetPassword: async (params: { email: string }): Promise<void> => {
    const { email } = params
    await dao._http.post<HttpResponse<void>>('/reset-password/', { email })
  },
  resetPassword: async (params: {
    email: string
    code: string
    clientGuid: string
    password: string
  }): Promise<AuthDaoRegisterResult> => {
    const { email, code, clientGuid, password } = params
    const result = await dao._http.post<HttpResponse<AuthDaoRegisterResult>>(`/reset-password/${code}`, {
      email,
      clientGuid,
      password,
    })
    return result.data.data[0]
  },
  verifyEmail: async (params: { code: string }): Promise<void> => {
    const { code } = params
    await dao._http.post<HttpResponse<void>>(`/verify-email/${code}`)
  },
  requestEmailVerification: async (params: { userId: string; clientGuid: string }): Promise<void> => {
    const { userId, clientGuid } = params
    await dao._http.post<HttpResponse<void>>('/verify-email', { userId, clientGuid })
  },
  updateUserNameAndLegals: async (params: { name: string; acceptAllLegals?: boolean }): Promise<void> => {
    const { name, acceptAllLegals = false } = params
    await dao._http.patch<HttpResponse<User>>('/users/me', { name }, { params: { acceptAllLegals } })
  },
  agreeToLatestLegalAgreements: async (): Promise<void> => {
    const url = '/users/me/legal-agreements/latest'
    await dao._http.post(url)
  },
  getUserMe: async (): Promise<User> => {
    const url = `/users/me`
    const result = await dao._http.get<HttpResponse<User>>(url)
    return result.data.data[0]
  },
  signInWithApple: async (params: { redirectUrl: string }): Promise<{ url: string }> => {
    return { url: `/api/auth/auth/apple?redirectUrl=${encodeURIComponent(params.redirectUrl)}` }
  },
  fulfillImportWelcome: async (params: {
    email: string
    code: string
    clientGuid: string
    password: string
  }): Promise<AuthDaoRegisterResult> => {
    const { email, code, clientGuid, password } = params
    const result = await dao._http.post<HttpResponse<AuthDaoRegisterResult>>(`/users/import/${code}`, {
      email,
      clientGuid,
      password,
    })
    return result.data.data[0]
  },
}
